import { useAtom, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  districtIdAtom,
  districtIngestionIdAtom,
  districtIngestionResponseAtom,
  districtResponseAtom,
} from './district.atoms'
import { useEffect, useMemo, useState } from 'react'
import type { ResourceQuery } from 'infrastructure/api'
import { doAdminMutation, reinstateDistricts } from '../providers/mutations'
import {
  makeKiddomPatchRequest,
  makeKiddomPostRequest,
} from 'infrastructure/api'
import { ISyncLimitUpdate } from 'infrastructure/api'

export function addDistrictFeature(params: {
  district_id: number
  feature_id: number
  locked: boolean
}) {
  const queryData: ResourceQuery = {
    key: 'addDistrictFeature',
    fields: ['id'],
  }
  return doAdminMutation(queryData, params)
}

export function removeDistrictFeature(params: {
  district_id: number
  feature_id: number
}) {
  const queryData: ResourceQuery = {
    key: 'removeDistrictFeature',
    fields: ['id'],
  }
  return doAdminMutation(queryData, params)
}

export function useDistrictUpdate(districtId: string | number | undefined) {
  const id = Number(districtId)
  useSetAtom(districtIdAtom)(id)
  const [updateData, setUpdateData] = useState({
    name: '',
    is_verified: false,
  })
  const [atomData, reFetchDistrict] = useAtom(districtResponseAtom)
  useEffect(() => {
    if (!atomData) {
      setUpdateData({
        name: '',
        is_verified: false,
      })
      return
    }
    setUpdateData({
      name: atomData.name,
      is_verified: atomData.is_verified,
    })
  }, [setUpdateData, atomData])
  return useMemo(() => {
    return {
      reFetchDistrict,
      updateData: {
        ...updateData,
        id: atomData ? parseInt(atomData.id) : 0,
      },
      setUpdateData,
    }
  }, [id, atomData, reFetchDistrict, updateData])
}

export function useDistrictResource(districtId: string | number | undefined) {
  const id = Number(districtId)
  useSetAtom(districtIdAtom)(id)
  const [atomData, reFetchDistrict] = useAtom(districtResponseAtom)
  return useMemo(() => {
    return {
      data: atomData,
      reFetchDistrict,
      updateSyncSettings: async (params: { enable_daily_sync: boolean }) => {
        if (!districtId) return
        await makeKiddomPostRequest({
          kiddomPath: `/v1/district/${Number(id)}/sis/sync/settings`,
          requestBody: params,
        })
      },
      addDistrictFeature: async (params: {
        feature_id: number
        locked: boolean
      }) => {
        await addDistrictFeature({ district_id: id, ...params })
        reFetchDistrict()
      },
      removeDistrictFeature: async (feature_id: number) => {
        await removeDistrictFeature({ district_id: id, feature_id })
        reFetchDistrict()
      },
      reinstate: async () => {
        await reinstateDistricts({
          district_ids: [id],
          reinstate_schools: true,
          reinstate_users: true,
        })
        reFetchDistrict()
      },
      updateSyncLimitsSettings: async (params: ISyncLimitUpdate) => {
        const { result, error } = await makeKiddomPatchRequest({
          kiddomPath: `/v1/kat/districts/sync/settings`,
          requestBody: params,
        })
        if (error != undefined) {
          throw error
        }
        return result
      },
    }
  }, [id, atomData, reFetchDistrict])
}

export function useDistrictIngestionResource(districtId: string | number) {
  useSetAtom(districtIngestionIdAtom)(districtId)
  const [atomData, reFetchIngestion] = useAtom(districtIngestionResponseAtom)
  return useMemo(() => {
    return {
      data: atomData,
      reFetchIngestion,
    }
  }, [atomData, reFetchIngestion])
}
